/* global axios */

import { throwErrorMessage } from 'dashboard/store/utils/api';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import CannedResponseAPI, { CannedResponseParams } from '../../api/cannedResponse';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import axios from 'axios';

export interface CannedResponse {
  id: number;
  short_code: string;
  content: string;
  team_ids: number[];
}

export interface CannedResponseUIFlags {
  fetchingList: boolean;
  fetchingItem: boolean;
  creatingItem: boolean;
  updatingItem: boolean;
  deletingItem: boolean;
}

export interface CannedResponseState {
  records: CannedResponse[];
  uiFlags: CannedResponseUIFlags;
}

const state: CannedResponseState = {
  records: [],
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    deletingItem: false,
  },
};

const getters: GetterTree<CannedResponseState, any> = {
  getCannedResponses(_state): CannedResponse[] {
    return _state.records;
  },
  getSortedCannedResponses(_state) {
    return (sortOrder: 'asc' | 'desc'): CannedResponse[] =>
      [..._state.records].sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.short_code.localeCompare(b.short_code);
        }
        return b.short_code.localeCompare(a.short_code);
      });
  },
  getUIFlags(_state): CannedResponseUIFlags {
    return _state.uiFlags;
  },
};

let abortController: AbortController | null = null;

const actions: ActionTree<CannedResponseState, any> = {
  getCannedResponse: async ({ commit }, params: CannedResponseParams) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: true });

    try {
      const response = await CannedResponseAPI.get(params, {
        signal: abortController.signal,
      });

      commit(types.default.SET_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
      }
    }
  },
  createCannedResponse: async (
    { commit },
    cannedObj: Partial<CannedResponse>
  ) => {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const response = await CannedResponseAPI.create(cannedObj);
      commit(types.default.ADD_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },

  updateCannedResponse: async (
    { commit },
    { id, ...updateObj }: Partial<CannedResponse> & { id: number }
  ) => {
    commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: true });
    try {
      const response = await CannedResponseAPI.update(id, updateObj);
      commit(types.default.EDIT_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return throwErrorMessage(error);
    }
  },

  deleteCannedResponse: async ({ commit }, id: number) => {
    commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
    try {
      await CannedResponseAPI.delete(id);
      commit(types.default.DELETE_CANNED, id);
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return id;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return throwErrorMessage(error);
    }
  },
};

const mutations: MutationTree<CannedResponseState> = {
  [types.default.SET_CANNED_UI_FLAG](
    _state,
    data: Partial<CannedResponseUIFlags>
  ) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_CANNED]: MutationHelpers.set,
  [types.default.ADD_CANNED]: MutationHelpers.create,
  [types.default.EDIT_CANNED]: MutationHelpers.update,
  [types.default.DELETE_CANNED]: MutationHelpers.destroy,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
